'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@shared/utils';
import { Button } from '../buttons';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../overlay/popover';
import { Input } from './Input';
import type { DateTimePickerProps } from '../../types/DateTimePickerProps';

/**
 * @group Components
 * @summary <blockquote>DateTimePicker component for selecting date and time.</blockquote>
 * @example
 * ```tsx
 * <DateTimePicker 
 *   value={selectedDate} 
 *   onChange={(date) => setSelectedDate(date)} 
 * />
 * ```
 */
export function DateTimePicker({
  value,
  onChange,
  disabled
}: DateTimePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(value);
  const [isOpen, setIsOpen] = React.useState(false);

  // Sync internal date state when value prop changes
  React.useEffect(() => {
    setDate(value);
  }, [value]);

  // Handle date selection, preserving time part if available
  const handleSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      if (date) {
        newDate.setHours(date.getHours(), date.getMinutes(), date.getSeconds());
      }
      setDate(newDate);
      onChange?.(newDate);
    }
  };

  // Handle time change, updating the time portion of the date
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!date) return;
    const [hours, minutes] = e.target.value.split(':').map(Number);
    const newDate = new Date(date);
    newDate.setHours(hours || 0, minutes || 0, 0);
    setDate(newDate);
    onChange?.(newDate);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground'
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, 'PPp') : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="space-y-4 p-3">
          <DayPicker
            mode="single"
            selected={date}
            onSelect={handleSelect}
            disabled={disabled}
          />
          <div className="px-3 pb-3">
            <Input
              type="time"
              value={date ? format(date, 'HH:mm') : ''}
              onChange={handleTimeChange}
              disabled={disabled || !date}
              step="1"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
